import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fadeUp, viewport } from "@/lib/motion";

/**
 * The site's design kit — the pieces every page composes from.
 *
 * Colour never appears here as a literal. Every class is a palette token from
 * `src/index.css` (`brand`, `gold`, `content`, `line`, `valid`/`warn`/`danger`),
 * the same tokens the product app uses, so a channel changed there moves the
 * site with it. Tone names are the app's too — `brand` not `violet`, `valid`
 * not `green` — so a component can be lifted between the two codebases.
 */

/* ---------- Layout ---------- */
// The page gutter. 20px on a phone rather than 24 — on a 360px screen those
// 8px are the difference between a status row fitting and wrapping.
export function Container({ className = "", children }) {
  return <div className={`mx-auto w-full max-w-[1240px] px-5 sm:px-6 md:px-10 ${className}`}>{children}</div>;
}

export function Section({ id, className = "", children }) {
  return (
    <section id={id} className={`relative py-16 sm:py-24 md:py-32 lg:py-40 ${className}`}>
      {children}
    </section>
  );
}

/* ---------- Typographic ---------- */
export function Eyebrow({ children, tone = "brand", className = "" }) {
  const dot = { brand: "bg-brand", gold: "bg-gold", valid: "bg-valid" }[tone] || "bg-brand";
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-content-muted">{children}</span>
    </div>
  );
}

export function DisplayHeading({ children, className = "", as: Tag = "h2" }) {
  return (
    <Tag className={`font-display font-medium tracking-tight text-content ${className}`}>{children}</Tag>
  );
}

export function BodyCopy({ children, className = "" }) {
  return <p className={`text-[16px] leading-relaxed text-content-muted sm:text-[17px] md:text-lg ${className}`}>{children}</p>;
}

export function TechLabel({ children, className = "" }) {
  return (
    <span className={`font-mono text-[10px] uppercase tracking-[0.24em] text-content-faint ${className}`}>
      {children}
    </span>
  );
}

/* ---------- Buttons ---------- */
// Marketing proportions — a pill, roomier than the product app's rounded-md
// control — on the app's fills.
//
// Every call to action on the site is `primary`, i.e. brand blue. `gold` is
// kept for the one thing gold is for — an action that issues proof, the way
// the app reserves it for generating a readiness report — and is currently
// unused. It is not a second accent: reach for it and a page grows a brown
// button.
const btnBase =
  "inline-flex min-h-[44px] items-center justify-center gap-2 rounded-pill text-center text-sm font-medium transition-colors duration-300 focus-visible:outline-none disabled:opacity-50";

export function Button({ children, to, href, variant = "primary", className = "", ...props }) {
  const styles = {
    primary: "bg-brand text-ink hover:bg-brand-soft shadow-lift-brand px-6 py-3",
    gold: "bg-gold text-ink font-semibold hover:bg-gold-bright shadow-lift-gold px-6 py-3",
    outline: "border border-line bg-panel text-content hover:border-brand/50 hover:bg-panel-raised px-6 py-3",
    ghost: "text-content-muted hover:text-content px-3 py-2",
  }[variant];
  const cls = `${btnBase} ${styles} ${className}`;
  if (to) return <Link to={to} className={cls} {...props}>{children}</Link>;
  if (href) return <a href={href} className={cls} {...props}>{children}</a>;
  return <button className={cls} {...props}>{children}</button>;
}

export function TextLink({ children, to, href, className = "", ...props }) {
  // min-h so the target clears 44px on touch — the text itself is only 20px tall
  const cls = `group inline-flex min-h-[44px] items-center gap-1.5 text-sm font-medium text-content transition-colors hover:text-brand ${className}`;
  const inner = (
    <>
      {children}
      <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
    </>
  );
  if (to) return <Link to={to} className={cls} {...props}>{inner}</Link>;
  return <a href={href} className={cls} {...props}>{inner}</a>;
}

/* ---------- Badges ---------- */
// The product app's tone map, to the letter (APP ui/badges.tsx). Gold is proof
// only — a certificate, a verified state, a trust score — never decoration.
const TONE_STYLES = {
  neutral: "border-line bg-panel-raised text-content-muted",
  brand: "border-brand/40 bg-brand/10 text-brand",
  valid: "border-valid/40 bg-valid/10 text-valid",
  warn: "border-warn/40 bg-warn/10 text-warn",
  danger: "border-danger/40 bg-danger/10 text-danger",
  gold: "border-gold/50 bg-gold/10 text-gold-bright",
  // A standard's name, set as data rather than as a status.
  framework: "border-line bg-panel-raised text-content-muted font-mono tracking-wide",
};

export function Badge({ children, tone = "neutral", className = "" }) {
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-pill border px-3 py-1 text-xs font-medium ${TONE_STYLES[tone] || TONE_STYLES.neutral} ${className}`}
    >
      {children}
    </span>
  );
}

// A pipeline state, as a dot plus a word. Each maps to a tone above rather than
// carrying its own colours, so a status can never drift from the badge palette.
const statusMap = {
  verified: { tone: "valid", dot: "bg-valid", label: "Verified" },
  passed: { tone: "valid", dot: "bg-valid", label: "Passed" },
  finding: { tone: "warn", dot: "bg-warn", label: "Finding" },
  risk: { tone: "danger", dot: "bg-danger", label: "Risk" },
  active: { tone: "brand", dot: "bg-brand", label: "Active" },
};

export function StatusBadge({ status = "verified", children, className = "" }) {
  const s = statusMap[status] || statusMap.verified;
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-pill border px-3 py-1 font-mono text-[11px] uppercase tracking-[0.14em] ${TONE_STYLES[s.tone]} ${className}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {children || s.label}
    </span>
  );
}

/* ---------- Panels ---------- */
export function Panel({ children, className = "", premium = false, interactive = false }) {
  return (
    <div
      className={`relative overflow-hidden rounded-panel border ${premium ? "border-gold/30 bg-panel shadow-ring-gold" : "border-line bg-panel shadow-panel"} ${interactive ? "card-hover" : ""} ${className}`}
    >
      {children}
    </div>
  );
}

export function Metric({ value, label, sub, tone = "content", className = "" }) {
  const c = {
    content: "text-content",
    gold: "text-gradient-gold",
    valid: "text-valid",
    brand: "text-brand",
  }[tone];
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <span className={`font-display text-4xl font-light md:text-5xl ${c}`}>{value}</span>
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-content-faint">{label}</span>
      {sub && <span className="text-sm text-content-muted">{sub}</span>}
    </div>
  );
}

/* ---------- Reveal wrapper ---------- */
export function Reveal({ children, i = 0, className = "" }) {
  return (
    <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} custom={i} className={className}>
      {children}
    </motion.div>
  );
}
